// Каркас карты «Скаута»: карта, подложка, рубежи, вид в адресе, рамка кадра.
//
// До 28.10.2026 он был написан дважды — в «Осмотре» и в «Датасете», почти
// построчно. Слои раздела кладёт страница в `onLoad`: каркас не знает, что на
// нём рисуют, и потому не разъедется со страницей при правке её слоёв.

import { useEffect, useRef, useState, type RefObject } from 'react'
import maplibregl from 'maplibre-gl'

import { domainMaxBounds } from '../domain/limits'
import { releaseActiveMap, setActiveMap } from '../map/activeMap'
import { baseStyle } from '../map/baseStyle'
import { registerBasemapMap } from '../map/basemapRefresh'
import { useMapView } from '../map/mapView'
import { createStyleGate } from '../map/styleGate'
import {
  boxKey, boundsToBox, useBoundaryOverlays, type BoundaryToggles,
} from '../map/useBoundaryOverlays'

interface Options {
  containerRef: RefObject<HTMLDivElement | null>
  home: { center: [number, number]; zoom: number }
  /** Какие рубежи включены. */
  boundaries: BoundaryToggles
  /** Слой, ПОД который ложатся рубежи: рубеж над снимком, но не поверх судна. */
  boundaryBefore: string
  /** Источники и слои страницы — один раз, после загрузки стиля. */
  onLoad: (map: maplibregl.Map) => void
}

export function useScoutMap(o: Options) {
  const mapRef = useRef<maplibregl.Map | null>(null)
  const gateRef = useRef(createStyleGate())
  const [box, setBox] = useState<ReturnType<typeof boundsToBox> | null>(null)

  const { center, zoom, onMoveEnd } = useMapView()
  const moveEndRef = useRef(onMoveEnd)
  moveEndRef.current = onMoveEnd
  const onLoadRef = useRef(o.onLoad)
  onLoadRef.current = o.onLoad
  const initialViewRef = useRef({ center: center ?? o.home.center, zoom: zoom ?? o.home.zoom })

  useEffect(() => {
    if (!o.containerRef.current) return
    const map = new maplibregl.Map({
      container: o.containerRef.current,
      style: baseStyle(),
      attributionControl: false,
      center: initialViewRef.current.center,
      zoom: initialViewRef.current.zoom,
      // Рамка домена: карта не уводится туда, где сервер откажет.
      maxBounds: domainMaxBounds(),
    })
    mapRef.current = map
    setActiveMap(map)
    const gate = createStyleGate()
    gateRef.current = gate
    map.dragRotate.disable()
    map.touchZoomRotate.disableRotation()
    map.keyboard.disableRotation()
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')

    map.on('moveend', () => {
      const c = map.getCenter()
      moveEndRef.current({ lng: c.lng, lat: c.lat, zoom: map.getZoom() })
      setBox(boundsToBox(map))
    })
    map.on('load', () => {
      onLoadRef.current(map)
      setBox(boundsToBox(map))
      gate.markReady()
    })

    const unregisterBasemap = registerBasemapMap(map)
    return () => {
      unregisterBasemap()
      releaseActiveMap(map)
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const boundaryMeta = useBoundaryOverlays({
    mapRef, gateRef, on: o.boundaries, viewKey: boxKey(box), beforeId: o.boundaryBefore,
  })

  return { mapRef, gateRef, box, boundaryMeta }
}
